import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Copy, Play, Trash2, RefreshCw, CheckCircle2, XCircle, Zap, Image as ImageIcon, FileCode,
} from "lucide-react";

const SELECT = "mt-2 w-full rounded-none border border-input bg-secondary px-3 py-2 text-sm";

const copy = (text, label) => {
  const p = navigator.clipboard?.writeText(text);
  if (!p) {
    toast.error("Clipboard blocked — select and copy manually");
    return;
  }
  p.then(() => toast.success(`${label} copied`)).catch(() =>
    toast.error("Clipboard blocked — select and copy manually")
  );
};

const CodeBlock = ({ code, label }) => (
  <div>
    <div className="flex items-center justify-between border border-border border-b-0 bg-secondary/60 px-3 py-2">
      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      <button type="button" onClick={() => copy(code, label)} className="text-primary"
        data-testid={`copy-snippet-${label.replace(/\W+/g, "-").toLowerCase()}`}>
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
    <pre className="mono max-h-80 overflow-auto border border-border bg-background/60 p-4 text-[11px] leading-relaxed text-foreground/90">
      {code}
    </pre>
  </div>
);

export const QuestIntegration = ({ campaigns }) => {
  const [campaignId, setCampaignId] = useState("");
  const [allQuests, setAllQuests] = useState(null);
  const [questId, setQuestId] = useState("");
  const [kit, setKit] = useState(null);
  const [snippet, setSnippet] = useState("curl");
  const [click, setClick] = useState(null);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/quests").then(({ data }) => setAllQuests(data));
  }, []);

  useEffect(() => {
    if (campaignId || campaigns.length === 0 || allQuests === null) return;
    const withQuests = campaigns.find((c) => allQuests.some((q) => q.campaign_id === c.id));
    setCampaignId((withQuests || campaigns[0]).id);
  }, [campaigns, campaignId, allQuests]);

  const quests = (allQuests || []).filter((q) => q.campaign_id === campaignId);

  useEffect(() => {
    setQuestId((cur) => (quests.some((q) => q.id === cur) ? cur : quests[0]?.id || ""));
  }, [campaignId, allQuests]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadKit = useCallback(async () => {
    if (!campaignId) return;
    try {
      const { data } = await api.get(`/admin/campaigns/${campaignId}/integration`);
      setKit(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [campaignId]);

  useEffect(() => {
    if (campaignId) {
      loadKit();
      setClick(null);
      setResults([]);
    }
  }, [campaignId, loadKit]);

  const mintClick = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/quest-test-click", { quest_id: questId });
      setClick(data);
      setResults([]);
      toast.success("Test click minted — fire a conversion against it");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const log = (row) => setResults((r) => [{ ...row, at: new Date().toLocaleTimeString() }, ...r]);

  const fire = async (label, url) => {
    setBusy(true);
    try {
      const res = await fetch(url);
      const text = await res.text();
      log({ label, status: res.status, ok: res.ok, body: text.slice(0, 300), url });
    } catch (e) {
      log({ label, status: 0, ok: false, body: String(e), url });
    } finally {
      setBusy(false);
    }
  };

  // a converted click can only ever answer "duplicate", so these tests get their own fresh click
  const fireFresh = async (label, key, isPixel) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/quest-test-click", { quest_id: questId });
      setClick(data);
      if (isPixel) await firePixel(label, data[key]);
      else {
        const res = await fetch(data[key]);
        const text = await res.text();
        log({ label, status: res.status, ok: res.ok, body: text.slice(0, 300), url: data[key] });
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const firePixel = (label, url) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        log({ label, status: 200, ok: true, url, body: "1x1 gif returned (pixels always return the image — check the callbacks log for the verdict)" });
        resolve();
      };
      img.onerror = () => {
        log({ label, status: 0, ok: false, url, body: "pixel failed to load" });
        resolve();
      };
      img.src = url;
    });

  const wipe = async () => {
    try {
      const { data } = await api.delete("/admin/quest-test-data");
      toast.success(`Cleared ${data.attempts} test click(s) and ${data.logs} log rows`);
      setClick(null);
      setResults([]);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const active = kit?.snippets?.find((s) => s.id === snippet);

  return (
    <div className="space-y-8" data-testid="quest-integration">
      <div className="grid gap-4 border border-border bg-card p-5 md:grid-cols-2">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Campaign
          </Label>
          <select className={SELECT} value={campaignId} data-testid="integration-campaign"
            onChange={(e) => setCampaignId(e.target.value)}>
            {campaigns.length === 0 && <option value="">No campaigns yet</option>}
            {campaigns.map((c) => {
              const label = `${c.brand_name} — ${c.name} ($${c.cpa_amount} CPA)`;
              return <option key={c.id} value={c.id}>{label}</option>;
            })}
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Quest to test against
          </Label>
          <select className={SELECT} value={questId} data-testid="integration-quest"
            onChange={(e) => setQuestId(e.target.value)}>
            {quests.length === 0 && <option value="">No quests on this campaign</option>}
            {quests.map((q) => <option key={q.id} value={q.id}>{q.title}</option>)}
          </select>
          {quests.length === 0 && (
            <p className="mt-2 text-[11px] text-amber-300">
              This campaign has no quests yet — build one in the Quests tab, then come back to test
              the tracking.
            </p>
          )}
        </div>
        {kit && (
          <p className="text-xs text-muted-foreground md:col-span-2">
            Auth mode: <span className="mono text-foreground/80">{kit.auth_mode}</span> —{" "}
            {kit.needs_signature
              ? "a valid HMAC signature is required on every postback."
              : "the campaign key in the URL is the shared secret; the signature is optional."}{" "}
            Pixels are always signature-checked.
          </p>
        )}
      </div>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Zap className="h-4 w-4 text-primary" /> End-to-end test
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Mints a sandboxed click, then fires real calls at the live endpoint. Test conversions
              pay no points and never touch your revenue numbers.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-none" disabled={!questId || busy}
              onClick={mintClick} data-testid="mint-test-click">
              <RefreshCw className="mr-2 h-3.5 w-3.5" /> Mint test click
            </Button>
            <Button size="sm" variant="outline" className="rounded-none" onClick={wipe}
              data-testid="wipe-test-data">
              <Trash2 className="mr-2 h-3.5 w-3.5" /> Clear test data
            </Button>
          </div>
        </div>

        {click && (
          <div className="mt-5 border border-border bg-card p-5" data-testid="test-click-panel">
            <div className="grid gap-3 text-xs sm:grid-cols-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">click_id</div>
                <div className="mono mt-1 break-all">{click.click_id}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">sig</div>
                <div className="mono mt-1 break-all">{click.sig}</div>
              </div>
              {click.player_link && (
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Player link the brand receives
                  </div>
                  <div className="mono mt-1 break-all text-primary">{click.player_link}</div>
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Button size="sm" className="rounded-none" disabled={busy}
                onClick={() => fire("Signed postback", click.signed_postback)}
                data-testid="fire-signed-postback">
                <Play className="mr-2 h-3.5 w-3.5" /> Signed postback
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" disabled={busy}
                onClick={() => fire("Duplicate postback", click.signed_postback)}
                data-testid="fire-duplicate-postback">
                <Play className="mr-2 h-3.5 w-3.5" /> Fire again (duplicate)
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" disabled={busy}
                onClick={() => fireFresh("Unsigned postback", "unsigned_postback", false)}
                data-testid="fire-unsigned-postback">
                <Play className="mr-2 h-3.5 w-3.5" /> Unsigned postback
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" disabled={busy}
                onClick={() => fireFresh("Signed pixel", "signed_pixel", true)}
                data-testid="fire-signed-pixel">
                <ImageIcon className="mr-2 h-3.5 w-3.5" /> Signed pixel
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" disabled={busy}
                onClick={() => fireFresh("Unsigned pixel", "unsigned_pixel", true)}
                data-testid="fire-unsigned-pixel">
                <ImageIcon className="mr-2 h-3.5 w-3.5" /> Unsigned pixel
              </Button>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              Signed postback should return <span className="mono">ok</span>; firing it again should
              say <span className="mono">duplicate</span>.{" "}
              {click.auth_mode === "key"
                ? "On this campaign an unsigned postback is also valid — the campaign key is the secret."
                : "An unsigned postback should be rejected with 400 on this campaign."}{" "}
              Unsigned pixels never award anything (check the callbacks log in Revenue).
              The unsigned and pixel buttons each mint a fresh click so the result is meaningful.
            </p>

            {results.length > 0 && (
              <div className="mt-5 space-y-2" data-testid="test-results">
                {results.map((r, i) => (
                  <div key={`${r.label}-${i}`}
                    className={`border px-4 py-3 text-xs ${r.ok ? "border-emerald-400/40 bg-emerald-400/5" : "border-red-400/40 bg-red-400/5"}`}
                    data-testid={`test-result-${i}`}>
                    <div className="flex items-center gap-2">
                      {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                        : <XCircle className="h-3.5 w-3.5 text-red-400" />}
                      <span className="font-medium">{r.label}</span>
                      <span className="mono text-muted-foreground">HTTP {r.status}</span>
                      <span className="ml-auto text-muted-foreground">{r.at}</span>
                    </div>
                    <div className="mono mt-2 break-all text-[11px] text-muted-foreground">{r.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {kit && (
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-base font-semibold md:text-lg">
                <FileCode className="h-4 w-4 text-primary" /> Integration kit
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Copy-ready for {kit.brand_name || "the brand"}'s dev team — keys already filled in.
              </p>
            </div>
            <Button size="sm" variant="outline" className="rounded-none"
              onClick={() => copy(kit.brief, "Integration brief")} data-testid="copy-brief">
              <Copy className="mr-2 h-3.5 w-3.5" /> Copy the brief
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {kit.snippets.map((s) => (
              <button key={s.id} type="button" onClick={() => setSnippet(s.id)}
                data-testid={`snippet-tab-${s.id}`}
                className={`border px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] ${
                  snippet === s.id ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-4">
            {active && <CodeBlock code={active.code} label={active.label} />}
            <CodeBlock code={kit.brief} label="Plain-English brief" />
          </div>
        </section>
      )}
    </div>
  );
};
